import { useQueries, type UseQueryResult } from '@tanstack/react-query'

import { courtLists } from '../api/catalog.generated'
import { identityHeaders } from '../api/client'
import type { ModuleNode } from '../api/types'

/**
 * Everything waiting on the signed-in user, from every register that has one.
 *
 * CM-RPT-01 describes a single cross-module queue, and each register answers
 * for its own slice of it: five of them publish `<prefix>/court` today, and
 * each docstring says the report module will union these slices when it is
 * built. The overview showed one of the five, so the first screen after
 * signing in was answering "what is waiting on you" with a fifth of the truth
 * — and answering it confidently, which is worse than an empty card.
 *
 * Which slices exist is not written down here. `modules.yml` decides what a
 * site loads, so the list comes from the application's own OpenAPI document
 * via `courtLists()`, the same way the menu is built from `/session/me`. A
 * register added next month appears here with no change to this file, and a
 * register a site does not run cannot leave a dead query behind.
 *
 * ## Why the rows are read loosely
 *
 * The five slices agree on what a queue entry means and disagree on what to
 * call it. All five carry `action_key`, `reason_key`, `due_on`, `state` and
 * `overdue`; none of them names its record the same way as another:
 *
 * | register | identifier         | number           | what it is |
 * | -------- | ------------------ | ---------------- | ---------- |
 * | CM-RFI   | `rfi_id`           | `number`         | `subject`  |
 * | CM-SUB   | `submittal_id`     | `number`         | `title`    |
 * | CM-COR   | `item_id`          | `number`         | `subject`  |
 * | CM-CHG   | `change_event_id`  | `number`         | `title`    |
 * | CM-MTG   | `action_id`        | `meeting_number` | `body`     |
 *
 * So this reads the five fields they share and takes the record's own name
 * from whichever of the three it finds. That is not tidiness for its own sake:
 * a union is exactly what CM-RPT is promised to do, and it cannot be written
 * against five shapes. Chapter 12 carries the row; this is the client half of
 * the same problem, solved where it can be solved today.
 */

/** The one shape the overview renders, whichever register a row came from. */
export type CourtRow = {
  /** The register's route prefix, which is how a row finds its module. */
  prefix: string
  /** Stable enough to key a list: the register's own identifier for the row. */
  id: string
  /** The record's number, or an em dash when it has not been issued one. */
  number: string
  /** What the record is, in the words its own register uses. */
  label: string
  /** The message key for what this person owes. Never rendered raw. */
  actionKey: string
  reasonKey: string
  dueOn: string | null
  state: string
  overdue: boolean
}

/**
 * The keys the five registers use for a record's own identifier.
 *
 * `record_id` comes before `change_event_id`: the change module queues each
 * price, directive and order in a chain separately, and they all carry the
 * same change event. Read by that, two rows of one chain shared a key.
 */
const ID_KEYS = [
  'rfi_id',
  'submittal_id',
  'item_id',
  'record_id',
  'change_event_id',
  'action_id',
] as const

/** The keys they use for what the record is called. */
const LABEL_KEYS = ['subject', 'title', 'body'] as const

/** The keys they use for its number. */
const NUMBER_KEYS = ['number', 'meeting_number'] as const

function firstString(row: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string' && value !== '') return value
  }
  return null
}

/**
 * One queue entry, read defensively.
 *
 * A row missing every identifier is dropped rather than rendered with an
 * invented key: a queue entry nobody can open is a line that wastes the reader
 * and hides the ones underneath it. A row missing its label is kept — its
 * number and what it owes are the two things somebody acts on, and a register
 * that answers with a blank subject is still telling the truth about the work.
 */
export function readCourtRow(prefix: string, raw: unknown): CourtRow | null {
  if (typeof raw !== 'object' || raw === null) return null
  const row = raw as Record<string, unknown>
  const id = firstString(row, ID_KEYS)
  if (id === null) return null
  const actionKey = row.action_key
  if (typeof actionKey !== 'string') return null
  return {
    prefix,
    id,
    number: firstString(row, NUMBER_KEYS) ?? '—',
    label: firstString(row, LABEL_KEYS) ?? '',
    actionKey,
    reasonKey: typeof row.reason_key === 'string' ? row.reason_key : '',
    dueOn: typeof row.due_on === 'string' ? row.due_on : null,
    state: typeof row.state === 'string' ? row.state : '',
    overdue: row.overdue === true,
  }
}

async function fetchCourt(path: string, projectId: string): Promise<CourtRow[]> {
  const query = new URLSearchParams({ project_id: projectId })
  const response = await fetch(`${path}?${query.toString()}`, {
    headers: { Accept: 'application/json', ...identityHeaders() },
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const body: unknown = await response.json()
  if (!Array.isArray(body)) return []
  const prefix = path.slice(0, -'/court'.length)
  return body.map((raw) => readCourtRow(prefix, raw)).filter((row): row is CourtRow => row !== null)
}

export type CourtQueue = {
  rows: CourtRow[]
  pending: boolean
  /** How many registers refused or failed, so the card can say so. */
  failed: number
}

/**
 * Every slice, fetched in parallel and read as one list.
 *
 * One register being unreachable — a permission this person does not hold, a
 * module mid-restart — must not empty the card. Each query stands alone and
 * the failures are counted rather than thrown, because "three of your four
 * registers answered" is a true thing the reader can act on and a blank card
 * is not.
 *
 * Sorted by what is due soonest, with dateless rows last: "no deadline" is not
 * "due immediately", the same order the meetings module sorts its own actions.
 */
export function useCourt(modules: ModuleNode[], projectId: string): CourtQueue {
  const loaded = new Set(modules.map((module) => module.route_prefix))
  const paths = courtLists()
    .map((list) => list.path)
    .filter((path) => loaded.has(path.slice(0, -'/court'.length)))

  const results = useQueries({
    queries: paths.map((path) => ({
      queryKey: ['court', path, projectId],
      retry: false,
      queryFn: () => fetchCourt(path, projectId),
    })),
  }) as UseQueryResult<CourtRow[]>[]

  const rows = results.flatMap((result) => result.data ?? [])
  rows.sort((a, b) => {
    if (a.dueOn === b.dueOn) return a.number.localeCompare(b.number)
    if (a.dueOn === null) return 1
    if (b.dueOn === null) return -1
    return a.dueOn < b.dueOn ? -1 : 1
  })
  return {
    rows,
    pending: results.some((result) => result.isPending),
    failed: results.filter((result) => result.error).length,
  }
}
